import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ListMagnifyingGlass, CaretDown, CaretRight } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { API, AXIOS_SKIP_HTTP_CACHE } from '../config';
import { isAdmin } from '../utils/roles';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../components/ui/pagination';

function actionTone(action) {
  const a = String(action || '').toLowerCase();
  if (a.includes('delete')) return 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-800';
  if (a.includes('create') || a.includes('login')) return 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-800';
  if (a.includes('complete') || a.includes('mobile')) return 'bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800';
  return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600';
}

const AuditTrailPage = () => {
  const { t, language } = useLanguage();
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const locale = language === 'en' ? 'en-GB' : 'nl-NL';

  const loadActions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-logs/actions`, {
        withCredentials: true,
        ...AXIOS_SKIP_HTTP_CACHE,
      });
      setActions(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* optional */
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (actionFilter && actionFilter !== 'all') params.action = actionFilter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API}/admin/audit-logs`, {
        withCredentials: true,
        params,
        ...AXIOS_SKIP_HTTP_CACHE,
      });
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setTotal(res.data?.total ?? 0);
      setTotalPages(res.data?.total_pages ?? 1);
    } catch {
      toast.error(t('failedFetchData'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, actionFilter, search, t]);

  useEffect(() => {
    if (!isAdmin(currentUser)) {
      setLoading(false);
      return;
    }
    void loadActions();
  }, [currentUser, loadActions]);

  useEffect(() => {
    if (!isAdmin(currentUser)) return;
    void loadLogs();
  }, [currentUser, loadLogs]);

  useEffect(() => {
    setPage(1);
  }, [search, actionFilter]);

  if (!isAdmin(currentUser)) {
    return (
      <div className="animate-fadeIn flex items-center justify-center min-h-[40vh]">
        <p className="text-[#64748b]">{t('adminOnlyPage')}</p>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn" data-testid="audit-trail-page">
      <div className="mb-8">
        <h1 className="page-title flex items-center gap-2">
          <ListMagnifyingGlass size={28} weight="duotone" className="text-[#00CBA9]" />
          {t('auditTrail')}
        </h1>
        <p className="page-subtitle">
          {t('auditPageSubtitle')} · {t('auditTotal', total)}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-6">
        <input
          type="search"
          className="vmk-input flex-1"
          placeholder={t('auditSearchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="w-full lg:w-[280px]">
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="vmk-input h-11">
              <SelectValue placeholder={t('auditFilterAction')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('auditFilterAll')}</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a} value={a}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-center text-[#64748b] py-16">{t('auditNoEntries')}</p>
      ) : (
        <div className="space-y-2">
          {items.map((entry) => {
            const open = expandedId === entry.id;
            const when = entry.created_at
              ? new Date(entry.created_at).toLocaleString(locale)
              : '—';
            const actor =
              entry.actor_name ||
              entry.actor_email ||
              (entry.actor_type === 'mobile' ? 'Mobile' : '—');
            const entity =
              entry.entity_type && entry.entity_id
                ? `${entry.entity_type} #${entry.entity_id}`
                : entry.entity_type || '—';

            return (
              <div
                key={entry.id}
                className="vmk-card-flat overflow-hidden border border-[#e2e8f0] dark:border-[#334155]"
              >
                <button
                  type="button"
                  className="w-full flex items-start gap-3 p-4 text-left hover:bg-[#f8fafc] dark:hover:bg-[#1e293b]/50 transition-colors"
                  onClick={() => setExpandedId(open ? null : entry.id)}
                >
                  {open ? (
                    <CaretDown size={18} className="text-[#94a3b8] mt-0.5 shrink-0" />
                  ) : (
                    <CaretRight size={18} className="text-[#94a3b8] mt-0.5 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span
                        className={`inline-flex rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${actionTone(entry.action)}`}
                      >
                        {entry.action}
                      </span>
                      <span className="text-xs text-[#94a3b8] tabular-nums">{when}</span>
                      {entry.http_status ? (
                        <span className="text-xs text-[#94a3b8]">HTTP {entry.http_status}</span>
                      ) : null}
                    </div>
                    <p className="text-sm font-medium text-[#1a1a1a] dark:text-white truncate">
                      {entry.summary}
                    </p>
                    <p className="text-xs text-[#64748b] mt-1">
                      {t('auditActor')}: {actor} · {t('auditEntity')}: {entity}
                    </p>
                  </div>
                </button>
                {open && entry.details ? (
                  <div className="border-t border-[#e2e8f0] dark:border-[#334155] bg-[#f8fafc] dark:bg-[#0f172a] px-4 py-3">
                    <p className="text-xs font-semibold text-[#64748b] uppercase mb-2">
                      {t('auditDetails')}
                    </p>
                    <pre className="text-xs overflow-x-auto whitespace-pre-wrap break-words text-[#334155] dark:text-[#cbd5e1] font-mono">
                      {JSON.stringify(entry.details, null, 2)}
                    </pre>
                    {entry.http_path ? (
                      <p className="text-xs text-[#94a3b8] mt-2 font-mono">
                        {entry.http_method} {entry.http_path}
                        {entry.ip_address ? ` · ${entry.ip_address}` : ''}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-[#64748b]">
            {t('page')} {page} / {totalPages}
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.max(1, p - 1));
                  }}
                  className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive onClick={(e) => e.preventDefault()}>
                  {page}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                  className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default AuditTrailPage;
